import { useState, useRef, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import api from '../../lib/api';

export interface UploadedFile {
  id: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
}

interface FileUploadZoneProps {
  questionnaireId: string;
  section: string;
  uploads: UploadedFile[];
  onUploadsChange: (uploads: UploadedFile[]) => void;
}

export function FileUploadZone({ section, uploads, onUploadsChange }: FileUploadZoneProps) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      if (!files.length) return;
      setUploading(true);
      try {
        const formData = new FormData();
        Array.from(files).forEach((file) => formData.append('files', file));
        formData.append('section', section);

        const res = await api.post('/uploads', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        onUploadsChange([...res.data, ...uploads]);
      } catch (err) {
        console.error('Upload failed:', err);
        Sentry.captureException(err);
      } finally {
        setUploading(false);
      }
    },
    [section, uploads, onUploadsChange],
  );

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/uploads/${id}`);
      onUploadsChange(uploads.filter((u) => u.id !== id));
    } catch (err) {
      console.error('Delete failed:', err);
      Sentry.captureException(err);
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-3">
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
          ${dragOver ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-gray-400'}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFiles(e.dataTransfer.files);
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => e.target.files && handleFiles(e.target.files)}
        />
        {uploading ? (
          <p className="text-sm text-gray-500">Uploading...</p>
        ) : (
          <>
            <p className="text-sm text-gray-600">Drag & drop files here, or click to browse</p>
            <p className="text-xs text-gray-400 mt-1">
              PDF, DOCX, PowerPoint (.pptx), Markdown, CSV, TXT, and more (max 50MB).
            </p>
          </>
        )}
      </div>

      {uploads.length > 0 && (
        <ul className="divide-y divide-gray-100">
          {uploads.map((file) => (
            <li key={file.id} className="flex items-center justify-between py-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 truncate">{file.filename}</p>
                <p className="text-xs text-gray-500">{formatSize(file.sizeBytes)}</p>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(file.id);
                }}
                className="ml-3 text-sm text-red-600 hover:text-red-800"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
